import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Table, Tag, message, Alert, Descriptions, Progress, Space } from 'antd';
import { getBatchDetails, reprocessBatch, exportBatchSuccess, exportBatchFailure, exportBatchFull } from '../../services/api';
import styles from './BatchPage.module.css';

const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
};

const BatchDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const fetchDetails = useCallback(async (page = 1, pageSize = 10) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getBatchDetails(id, { page, limit: pageSize });
      console.log('Batch Details API Response:', response.data);
      setDetails(response.data);
      if (response.data.pagination) {
        setPagination({
          current: response.data.pagination.page,
          pageSize: response.data.pagination.limit,
          total: response.data.pagination.total,
        });
      } else if (response.data.transactions && typeof response.data.transactions === 'object' && !Array.isArray(response.data.transactions)) {
        setPagination({
          current: response.data.transactions.page || 1,
          pageSize: response.data.transactions.limit || 10,
          total: response.data.transactions.total || 0,
        });
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to load batch details.';
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const handleTableChange = (pagination) => {
    fetchDetails(pagination.current, pagination.pageSize);
  };

  const handleReprocess = async () => {
    setLoading(true);
    try {
      await reprocessBatch(id);
      message.success('Reprocessing failed transactions.');
      fetchDetails();
    } catch (err) {
      message.error(err.response?.data?.detail || 'Failed to reprocess.');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (type) => {
    try {
      let response;
      let filename;
      switch (type) {
        case 'success':
          response = await exportBatchSuccess(id);
          filename = `batch-${id}-success.csv`;
          break;
        case 'failure':
          response = await exportBatchFailure(id);
          filename = `batch-${id}-failure.csv`;
          break;
        case 'full':
          response = await exportBatchFull(id);
          filename = `batch-${id}-full-report.csv`;
          break;
        default:
          message.error('Invalid report type.');
          return;
      }
      downloadFile(response.data, filename);
      message.success('Report downloaded.');
    } catch (err) {
      message.error('Failed to download report.');
    }
  };

  const getStatusTag = (status) => {
    const s = status ? status.toUpperCase() : '';
    switch (s) {
      case 'PENDING': return <Tag color="orange">PENDING</Tag>;
      case 'PROCESSING': return <Tag color="blue">PROCESSING</Tag>;
      case 'SUCCESS':
      case 'COMPLETED': return <Tag color="green">COMPLETED</Tag>;
      case 'FAILED': return <Tag color="red">FAILED</Tag>;
      default: return <Tag>{status}</Tag>;
    }
  };

  const columns = [
    { title: 'Beneficiary Name', dataIndex: 'beneficiary_name', key: 'beneficiary_name' },
    { title: 'Account No', dataIndex: 'beneficiary_account', key: 'beneficiary_account' },
    { title: 'IFSC', dataIndex: 'ifsc', key: 'ifsc' },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (val) => `₹${val}` },
    { title: 'Status', dataIndex: 'status', key: 'status', render: getStatusTag },
    { 
      title: 'UTR / Error', 
      key: 'info', 
      render: (r) => {
        const s = r.status ? r.status.toUpperCase() : '';
        const value = (s === 'SUCCESS' || s === 'COMPLETED') ? r.utr : r.error_message;
        return value || 'None';
      }
    },
  ];

  if (loading && !details) {
    return <p>Loading...</p>;
  }

  if (error) {
    return <Alert message="Error" description={error} type="error" showIcon />;
  }

  if (!details) {
    return null;
  }

  const totalTxns = details.total_txns || details.total_transactions || 0;
  const successCount = details.success_count || 0;
  const failedCount = details.failed_count || 0;
  const progressPercent = totalTxns > 0 ? Math.round(((successCount + failedCount) / totalTxns) * 100) : 0;

  // Try to find the transactions array in common response properties
  // Added checks for .data inside the containers (common for paginated responses)
  const dataSource = Array.isArray(details.transactions) ? details.transactions : 
                     Array.isArray(details.transactions?.data) ? details.transactions.data :
                     Array.isArray(details.items) ? details.items : 
                     Array.isArray(details.items?.data) ? details.items.data :
                     Array.isArray(details.records) ? details.records : 
                     Array.isArray(details.records?.data) ? details.records.data :
                     Array.isArray(details.data) ? details.data : 
                     Array.isArray(details) ? details : [];

  return (
    <div className={styles.container}>
      <Button onClick={() => navigate('/batch-payouts')} style={{ marginBottom: 24 }}>
        &larr; Back to Batch List
      </Button>
      <div className={styles.detailsHeader}>
        <Descriptions title={`Batch Details: ${details.batch_ref_id || details.ref_id}`} bordered>
          <Descriptions.Item label="Status"><Tag color="blue">{details.status}</Tag></Descriptions.Item>
          <Descriptions.Item label="Total Amount">{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(details.total_amount)}</Descriptions.Item>
          <Descriptions.Item label="Transactions">{`${successCount} Success, ${failedCount} Failed of ${totalTxns}`}</Descriptions.Item>
        </Descriptions>
        <Progress percent={progressPercent} className={styles.progressBar} />
      </div>

      <Space style={{ marginBottom: 16 }}>
        <Button onClick={handleReprocess} disabled={details.failed_count === 0}>Reprocess Failed</Button>
        <Button onClick={() => handleExport('success')} disabled={details.success_count === 0}>Export Success Report</Button>
        <Button onClick={() => handleExport('failure')} disabled={details.failed_count === 0}>Export Failure Report</Button>
        <Button onClick={() => handleExport('full')} disabled={totalTxns === 0}>Export Full Report</Button>
      </Space>

      {totalTxns > 0 && dataSource.length === 0 && (
        <Alert
          message="No Transactions Found"
          description="The batch metadata indicates there are transactions, but the API response did not contain a list of transactions. Checked keys: transactions, items, records, data."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Table
        dataSource={dataSource}
        columns={columns}
        loading={loading}
        rowKey={(record) => record.id || record.row_number || record.reference_id || Math.random()}
        pagination={pagination}
        onChange={handleTableChange}
        className={styles.table}
      />
    </div>
  );
};
export default BatchDetails;